import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  WORK_ORDER_CATEGORIES,
  WORK_ORDER_CATEGORY_LABELS,
  WORK_ORDER_STATUSES,
  WORK_ORDER_STATUS_LABELS,
  type WorkOrderCategory,
  type WorkOrderStatus,
} from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { SearchField } from "@/components/app/patterns/search-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { optionalOneOf, KEEP_PLACE, searchPatch, optionalSearch } from "@/lib/search";
import { Plus } from "@/lib/icons";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import { orpc } from "@/lib/orpc";
import { usePaging } from "@/routes/_authenticated/menus/-lib/use-paging";
import { useMaintenanceBuilding } from "../-lib/use-maintenance-building";
import { MaintenanceBriefing } from "../-components/maintenance-briefing";
import { WorkOrderForm } from "../-components/work-order-form";
import { WorkOrderTable } from "../-components/work-order-table";

type StatusFilter = WorkOrderStatus | "open-any" | "closed-any" | "";
/** The address leaves out the default "open-any" and spells "every status" as "all". */
const STATUS_PARAMS = ["closed-any", "all", ...WORK_ORDER_STATUSES] as const;
type StatusParam = (typeof STATUS_PARAMS)[number];

export const Route = createFileRoute("/_authenticated/_maintenance/maintenance/work-orders/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    status?: StatusParam;
    category?: WorkOrderCategory;
  } => ({
    q: optionalSearch(search.q),
    status: optionalOneOf(STATUS_PARAMS, search.status),
    category: optionalOneOf(WORK_ORDER_CATEGORIES, search.category),
  }),
  component: WorkOrdersPage,
});

/** Every work order at the building, open ones first, with a filter and the door to file one. */
function WorkOrdersPage() {
  const { facilityId, canManage } = useMaintenanceBuilding();
  const { q, status: statusParam, category = "" } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = searchPatch(navigate);
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const status: StatusFilter =
    statusParam === undefined ? "open-any" : statusParam === "all" ? "" : statusParam;
  const setStatus = (next: StatusFilter) =>
    patch({ status: next === "open-any" ? undefined : next === "" ? "all" : next });
  const setCategory = (next: WorkOrderCategory | "") => patch({ category: next || undefined });
  const [filing, setFiling] = useState(false);
  const paging = usePaging(50);
  const orders = useQuery({
    ...orpc.agents.maintenance.workOrders.list.queryOptions({
      input: {
        facilityId,
        limit: paging.size,
        offset: paging.offset,
        ...(q ? { search: q } : {}),
        ...(status ? { status } : {}),
        ...(category ? { category } : {}),
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const searching = settling || orders.isPlaceholderData;
  const filtered = Boolean(search || category || status !== "open-any");
  const clear = () => {
    void navigate({ search: {}, ...KEEP_PLACE });
    paging.reset();
  };

  return (
    <section className="space-y-4">
      <MaintenanceBriefing facilityId={facilityId} />
      <div className="flex flex-wrap items-center gap-2">
        <SearchField
          className="max-w-xs sm:w-full"
          value={search}
          onChange={(next) => {
            setSearch(next);
            paging.reset();
          }}
          placeholder="Search work orders"
        />
        <OptionSelect
          aria-label="Status"
          value={status}
          className="w-44"
          onChange={(event) => {
            setStatus(event.target.value as StatusFilter);
            paging.reset();
          }}
        >
          <SelectOption value="open-any">Open</SelectOption>
          <SelectOption value="closed-any">Closed</SelectOption>
          <SelectOption value="">Every status</SelectOption>
          {WORK_ORDER_STATUSES.map((key) => (
            <SelectOption key={key} value={key}>
              {WORK_ORDER_STATUS_LABELS[key]}
            </SelectOption>
          ))}
        </OptionSelect>
        <OptionSelect
          aria-label="Category"
          value={category}
          className="w-48"
          onChange={(event) => {
            setCategory(event.target.value as WorkOrderCategory | "");
            paging.reset();
          }}
        >
          <SelectOption value="">Every category</SelectOption>
          {WORK_ORDER_CATEGORIES.map((key) => (
            <SelectOption key={key} value={key}>
              {WORK_ORDER_CATEGORY_LABELS[key]}
            </SelectOption>
          ))}
        </OptionSelect>
        {canManage ? (
          <div className="ml-auto">
            <Dialog open={filing} onOpenChange={setFiling}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="size-3.5" />
                  File work order
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>File a work order</DialogTitle>
                  <DialogDescription>
                    Say what is wrong and where. David reads it in context and prices the fix.
                  </DialogDescription>
                </DialogHeader>
                <WorkOrderForm
                  facilityId={facilityId}
                  onSaved={(workOrderId) => {
                    setFiling(false);
                    void navigate({
                      to: "/maintenance/work-orders/$workOrderId",
                      params: { workOrderId },
                    });
                  }}
                />
              </DialogContent>
            </Dialog>
          </div>
        ) : null}
      </div>

      {orders.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : orders.isError ? (
        <ReadFailed
          message="Couldn't load the work orders."
          error={orders.error}
          onRetry={() => void orders.refetch()}
        />
      ) : orders.data.rows.length === 0 && searching ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : orders.data.rows.length === 0 ? (
        <EmptyState
          title={filtered ? "No work order matches" : "Nothing is waiting"}
          description={
            filtered
              ? "Check the spelling, or clear the filters."
              : "When something breaks, file it here or tell David. He reads the order in context, prices the fix and readies the parts."
          }
          action={
            filtered ? (
              <Button variant="outline" onClick={clear}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <WorkOrderTable
          rows={orders.data.rows}
          paging={paging}
          total={orders.data.total}
          busy={searching}
        />
      )}
    </section>
  );
}
